import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import Alert from '../Alert';

export interface ValidationListItem {
  name: string;
  matcher: RegExp | ((value: string) => boolean) | boolean;
  /**
   * An optional error string to present to the consumer via the `errorCallback` prop on the
   * ValidationList component.  Falls back to the `name` value when the error field is not present.
   */
  error?: string;
}

type Level = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface ValidationListProps {
  /**
   * Use the accurate heading level to maintain the document outline.
   */
  as?: Level;

  /**
   * The heading displayed in the Alert.
   */
  title: string;
  className?: string;
  /**
   * The string to be validated based on the list of item rules.
   */
  targetValue: string;
  /**
   * The items to display in the list with names and matchers.
   */
  items: ValidationListItem[];
  /**
   * A callback for the parent component to check if all items are valid.
   */
  isValidCallback?: (isValid: boolean) => void;
  /**
   * A callback that returns an array of failed validation rule error messages.
   */
  errorCallback?: (errors: string[]) => void;
  /**
   * An ID to use on the list itself. Useful for providing to an aria-describedby attribute on another component.
   */
  id?: string;
}

const itemIsValid = (item: ValidationListItem, targetValue: string): boolean => {
  if (typeof item.matcher === 'function') return item.matcher(targetValue);
  if (typeof item.matcher === 'boolean') return item.matcher;
  return item.matcher.test(targetValue);
};

/**
 * The ValidationList component based on [the USWDS validation example](https://designsystem.digital.gov/components/form-controls/#validation).
 * To ensure accessibility, provide an `id` and use that same name for the `aria-describedby` attribute of your input field that
 * is being validated with the ValidationList.
 */
const ValidationList = ({
  as: headingLevel,
  title,
  className,
  targetValue,
  items,
  isValidCallback,
  errorCallback,
  id,
}: ValidationListProps): JSX.Element => {
  let allValid = true;

  const validatedItems = items.map((item) => {
    let valid = false;

    valid = itemIsValid(item, targetValue);

    if (!valid) {
      allValid = false;
    }

    return (
      // Disable lint rule so aria-checked can be applied to match USWDS component
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
      <li
        className={classNames('usa-checklist__item', { 'usa-checklist__item--checked': valid })}
        key={item.name.toLowerCase()}
        aria-checked={valid}
        aria-label={`${item.name} (${valid ? 'Checked' : 'Not Checked'})`}
      >
        {item.name}
      </li>
    );
  });

  const errors = useMemo(() => {
    const invalidItem = (item: ValidationListItem) => !itemIsValid(item, targetValue);
    const invalidItems = items.filter(invalidItem).map((item) => item?.error ?? item.name);
    return invalidItems;
  }, [items, targetValue]);

  useEffect(() => {
    if (isValidCallback instanceof Function) {
      isValidCallback(allValid);
    }
  }, [isValidCallback, allValid]);

  useEffect(() => {
    if (errorCallback instanceof Function) {
      errorCallback(errors);
    }
  }, [errorCallback, errors]);

  return (
    <Alert status="info" className={classNames('usa-alert--validation', className)}>
      <Alert.Heading as={headingLevel}>{title}</Alert.Heading>
      <ul className="usa-checklist" id={id}>
        {validatedItems}
      </ul>
    </Alert>
  );
};

export default ValidationList;
